/**
 * ============================================================================
 * BOOKING TRANSFORMATIONS - SINGLE SOURCE OF TRUTH
 * ============================================================================
 *
 * ⚠️  CRITICAL: This is the ONLY place where booking transformations happen!
 *
 * RULES:
 * 1. If you need to transform a booking, use a function from THIS file
 * 2. NEVER copy-paste transformation logic into components
 * 3. NEVER define formatTime or calculateDuration anywhere else
 * 4. If you add a new transformation, add it HERE, not in a component
 *
 * AMOUNT CALCULATION WARNING:
 * - ALWAYS use venue_share (what the venue earns)
 * - venue_share is stored as DECIMAL in DB, comes back as STRING
 * - Must parseFloat() before using
 * - DO NOT use final_price for vendor dashboard (that's what customer paid)
 *
 * RECURRING BOOKING LOGIC:
 * - Show NEXT upcoming session for active bookings
 * - Show LAST completed session for finished bookings
 * - Filter instances by scheduled_date >= today AND status = scheduled/confirmed
 * - If no upcoming sessions, booking is completed
 *
 * ============================================================================
 */

import type {
  OneTimeBooking,
  RecurringBooking,
  RecurringBookingInstance,
  BookingRow,
  ManualRecurringGroup,
  BookingStatus,
} from '../types/bookings';

// ============================================================================
// CSV REPORT GENERATION
// ============================================================================

/**
 * Escapes a value for CSV (quotes values containing commas, quotes, or newlines)
 */
function escapeCSV(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '""';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Generates and downloads a CSV booking report from BookingRow[] data.
 * Includes summary rows at the bottom with totals and breakdowns.
 * Used by Calendar (month) view.
 */
export function generateCalendarReportCSV(bookings: BookingRow[], title: string): void {
  if (!bookings || bookings.length === 0) return;

  const headers = [
    'Reference', 'Customer', 'Court', 'Sport', 'Date', 'Time',
    'Duration (mins)', 'Amount (NGN)', 'Venue Share (NGN)',
    'Payment Status', 'Booking Status', 'Source', 'Type',
    'Refund Amount', 'Refund Type',
  ];

  const rows = bookings.map((b) => {
    const raw = b.rawData as any;
    const venueShare = raw?.venue_share != null ? parseFloat(String(raw.venue_share)) : b.amount;
    const refundAmount = raw?.refund_amount != null ? parseFloat(String(raw.refund_amount)) : 0;
    const refundType = raw?.refund_type || '';

    return [
      b.reference,
      b.customer,
      b.court,
      b.sport,
      b.date,
      b.time,
      b.duration,
      b.amount,
      isNaN(venueShare) ? 0 : venueShare,
      b.paymentStatus,
      b.status,
      b.source || 'spotts',
      b.type === 'recurring' ? 'Recurring' :
        b.type === 'manual_recurring' ? 'Manual Recurring' :
        b.type === 'external' ? 'External' :
        b.type === 'blocked' ? 'Blocked' : 'One-time',
      isNaN(refundAmount) ? 0 : refundAmount,
      refundType,
    ];
  });

  // Calculate summary stats
  const totalRevenue = bookings.reduce((sum, b) => sum + (b.amount || 0), 0);
  const spottsCount = bookings.filter((b) => b.source === 'spotts').length;
  const manualCount = bookings.filter((b) => b.source === 'manual').length;
  const recurringCount = bookings.filter((b) => b.type === 'recurring' || b.type === 'manual_recurring').length;
  const cancelledCount = bookings.filter((b) => b.status === 'cancelled').length;
  const refunds = bookings.filter((b) => {
    const raw = b.rawData as any;
    return raw?.refund_amount && parseFloat(String(raw.refund_amount)) > 0;
  });
  const totalRefunded = refunds.reduce((sum, b) => {
    const raw = b.rawData as any;
    return sum + (parseFloat(String(raw.refund_amount)) || 0);
  }, 0);

  // Build CSV
  const summaryRows = [
    [],
    ['--- SUMMARY ---'],
    ['Total Bookings', bookings.length],
    ['Total Revenue (NGN)', totalRevenue.toLocaleString('en-NG')],
    ['Spotts Bookings', spottsCount],
    ['Manual Bookings', manualCount],
    ['Recurring Bookings', recurringCount],
    ['Cancelled Bookings', cancelledCount],
    ['Refunds Count', refunds.length],
    ['Total Refunded (NGN)', totalRefunded.toLocaleString('en-NG')],
  ];

  const csvContent = [
    [title],
    headers.map(escapeCSV).join(','),
    ...rows.map((row) => row.map(escapeCSV).join(',')),
    ...summaryRows.map((row) => row.map(escapeCSV).join(',')),
  ].join('\n');

  downloadCSV(csvContent, `${title.replace(/\s+/g, '-').toLowerCase()}.csv`);
}

/**
 * Generates and downloads a CSV booking report from day view timeline data.
 * Timeline bookings have a different shape than BookingRow.
 */
export function generateDayReportCSV(
  bookings: Array<{
    id: number;
    court_id: number;
    booking_reference: string;
    start_time: string;
    end_time: string;
    customer_name: string;
    sport_name: string;
    type: string;
    status: string;
    rawData: any;
  }>,
  title: string
): void {
  if (!bookings || bookings.length === 0) return;

  // Filter out blocked slots - only include actual bookings
  const actualBookings = bookings.filter((b) => b.type !== 'blocked');

  const headers = [
    'Reference', 'Customer', 'Court', 'Sport', 'Start Time', 'End Time',
    'Duration (mins)', 'Amount (NGN)', 'Venue Share (NGN)',
    'Payment Status', 'Booking Status', 'Source', 'Type',
    'Refund Amount', 'Refund Type',
  ];

  const rows = actualBookings.map((b) => {
    const raw = b.rawData;
    // For recurring instances, the price data is on the parent recurringBooking
    const parentBooking = raw?.recurringBooking || raw;
    const amount = parseFloat(String(parentBooking?.final_price || parentBooking?.venue_share || 0)) || 0;
    const venueShare = parseFloat(String(parentBooking?.venue_share || 0)) || 0;
    const refundAmount = parseFloat(String(parentBooking?.refund_amount || 0)) || 0;
    const refundType = parentBooking?.refund_type || '';
    const paymentStatus = parentBooking?.payment_status || '';
    const courtName = raw?.court_name || raw?.court?.name || parentBooking?.court?.name || '';

    const startMins = timeToMinutesSimple(b.start_time);
    const endMins = timeToMinutesSimple(b.end_time);
    let duration = endMins - startMins;
    if (duration <= 0) duration += 24 * 60;

    return [
      b.booking_reference,
      b.customer_name,
      courtName,
      b.sport_name,
      b.start_time,
      b.end_time,
      duration,
      amount,
      venueShare,
      paymentStatus,
      b.status,
      b.type === 'manual' ? 'manual' : b.type === 'external' ? 'external' : 'spotts',
      b.type === 'recurring' ? 'Recurring' : b.type === 'external' ? 'External' : b.type === 'manual' ? 'Manual' : 'One-time',
      refundAmount,
      refundType,
    ];
  });

  // Calculate summary stats
  const totalRevenue = rows.reduce((sum, row) => sum + (Number(row[7]) || 0), 0);
  const spottsCount = actualBookings.filter((b) => b.type === 'spotts').length;
  const manualCount = actualBookings.filter((b) => b.type === 'manual').length;
  const recurringCount = actualBookings.filter((b) => b.type === 'recurring').length;
  const externalCount = actualBookings.filter((b) => b.type === 'external').length;
  const cancelledCount = actualBookings.filter((b) => b.status === 'cancelled').length;
  const refunds = rows.filter((row) => Number(row[13]) > 0);
  const totalRefunded = refunds.reduce((sum, row) => sum + (Number(row[13]) || 0), 0);

  const summaryRows = [
    [],
    ['--- SUMMARY ---'],
    ['Total Bookings', actualBookings.length],
    ['Total Revenue (NGN)', totalRevenue.toLocaleString('en-NG')],
    ['Spotts Bookings', spottsCount],
    ['Manual Bookings', manualCount],
    ['Recurring Bookings', recurringCount],
    ['External Bookings', externalCount],
    ['Cancelled Bookings', cancelledCount],
    ['Refunds Count', refunds.length],
    ['Total Refunded (NGN)', totalRefunded.toLocaleString('en-NG')],
  ];

  const csvContent = [
    [title],
    headers.map(escapeCSV).join(','),
    ...rows.map((row) => row.map(escapeCSV).join(',')),
    ...summaryRows.map((row) => row.map(escapeCSV).join(',')),
  ].join('\n');

  downloadCSV(csvContent, `${title.replace(/\s+/g, '-').toLowerCase()}.csv`);
}

/** Simple time-to-minutes helper for HH:MM strings */
function timeToMinutesSimple(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Downloads a string as a CSV file */
function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Removes seconds from time string (HH:MM:SS -> HH:MM)
 */
export function formatTimeWithoutSeconds(time: string | null | undefined): string {
  if (!time || time === 'N/A') return 'N/A';
  return time.substring(0, 5);
}

/**
 * Calculates duration in minutes from HH:MM:SS time strings
 */
export function calculateDuration(startTime: string, endTime: string): number {
  if (!startTime || !endTime) return 0;

  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) {
    return 0;
  }

  const startTotalMin = startHour * 60 + startMin;
  const endTotalMin = endHour * 60 + endMin;

  let duration = endTotalMin - startTotalMin;
  if (duration <= 0) duration += 24 * 60; // Handle overnight wrap-around
  return duration;
}

/**
 * Gets the customer name from booking (handles manual bookings, customer, and user fields)
 */
export function getCustomerName(booking: OneTimeBooking | RecurringBooking): string {
  // Manual bookings have customer_name directly on the booking
  if ('customer_name' in booking && booking.customer_name) {
    return booking.customer_name;
  }
  // Spotts bookings have customer or user relationship
  return booking.customer?.name || booking.user?.name || 'Unknown';
}

/**
 * Gets the sport name from booking
 */
export function getSportName(booking: OneTimeBooking | RecurringBooking): string {
  return booking.court?.sport?.name || 'N/A';
}

/**
 * Gets the court name from booking
 */
export function getCourtName(booking: OneTimeBooking | RecurringBooking): string {
  return booking.court?.name || 'N/A';
}

/**
 * Safely parses amount to number
 * CRITICAL: Use venue_share for vendor dashboard (what vendor earns)
 * NOT final_price (what customer pays)
 */
export function parseAmount(booking: OneTimeBooking | RecurringBooking): number {
  // venue_share can be string (from DB DECIMAL) or number
  const venueShare = typeof booking.venue_share === 'string'
    ? parseFloat(booking.venue_share)
    : booking.venue_share;

  if (!isNaN(venueShare) && venueShare > 0) {
    return venueShare;
  }

  // Fallback to final_price if venue_share not available
  const finalPrice = typeof booking.final_price === 'string'
    ? parseFloat(booking.final_price)
    : booking.final_price;

  return !isNaN(finalPrice) ? finalPrice : 0;
}

/**
 * Transforms a one-time booking to a BookingRow
 */
export function transformOneTimeBooking(booking: OneTimeBooking): BookingRow {
  const duration = booking.duration_minutes || calculateDuration(booking.start_time, booking.end_time);

  return {
    id: booking.id,
    reference: booking.booking_reference || `BK-${booking.id}`,
    sport: getSportName(booking),
    customer: getCustomerName(booking),
    court: getCourtName(booking),
    date: booking.booking_date || 'N/A',
    time: `${formatTimeWithoutSeconds(booking.start_time)} - ${formatTimeWithoutSeconds(booking.end_time)}`,
    duration,
    amount: parseAmount(booking),
    status: booking.status,
    paymentStatus: booking.payment_status,
    type: 'onetime',
    source: booking.source === 'manual' ? 'manual' : 'spotts',
    createdAt: booking.created_at || '',
    rawData: booking,
  };
}

/**
 * Counts cancelled sessions in a recurring booking
 */
export function countCancelledSessions(instances: RecurringBookingInstance[] | undefined): number {
  if (!instances || instances.length === 0) {
    return 0;
  }
  return instances.filter(inst => inst.status === 'cancelled').length;
}

/**
 * Gets the display session for a recurring booking (next upcoming or last completed)
 */
export function getDisplaySession(
  instances: RecurringBookingInstance[] | undefined,
  today: string
): { session: RecurringBookingInstance | null; isCompleted: boolean } {
  if (!instances || instances.length === 0) {
    return { session: null, isCompleted: false };
  }

  const sortedInstances = [...instances].sort((a, b) =>
    a.scheduled_date.localeCompare(b.scheduled_date)
  );

  // Find next upcoming session.
  // For future dates: include any non-cancelled instance.
  // For today: use time-based check — end_time '00:00:00' means midnight (always upcoming on start date).
  // This guards against the backend bug where midnight bookings get prematurely marked 'completed'.
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const upcomingInstances = sortedInstances.filter((inst) => {
    if (inst.status === 'cancelled') return false;
    if (inst.scheduled_date > today) return true;
    if (inst.scheduled_date < today) return false;
    // Today: check if end time is still in the future
    const endTime = inst.scheduled_end_time || '';
    // '00:00:00' = midnight end (crosses into next day) — always upcoming on its start date
    if (!endTime || endTime.startsWith('00:00')) return true;
    const [eh, em] = endTime.split(':').map(Number);
    return (eh * 60 + (em || 0)) > nowMinutes;
  });

  if (upcomingInstances.length > 0) {
    return { session: upcomingInstances[0], isCompleted: false };
  }

  // No upcoming sessions - find most recent past session
  const pastInstances = sortedInstances
    .filter((inst) => inst.scheduled_date < today)
    .reverse();

  if (pastInstances.length > 0) {
    return { session: pastInstances[0], isCompleted: true };
  }

  // Fallback to any session
  return { session: sortedInstances[0], isCompleted: true };
}

/**
 * Transforms a recurring booking to a BookingRow
 */
export function transformRecurringBooking(
  booking: RecurringBooking,
  today: string
): BookingRow | null {
  const { session: displaySession, isCompleted } = getDisplaySession(booking.instances, today);

  if (!displaySession) {
    return null; // No sessions to display
  }

  const duration = calculateDuration(
    displaySession.scheduled_start_time,
    displaySession.scheduled_end_time
  );

  // Count cancelled sessions for partial cancellation badge
  const cancelledCount = countCancelledSessions(booking.instances);
  const totalSessions = booking.total_sessions || 0;

  return {
    id: booking.id,
    reference: booking.booking_reference || `REC-${booking.id}`,
    sport: getSportName(booking),
    customer: getCustomerName(booking),
    court: getCourtName(booking),
    date: displaySession.scheduled_date,
    time: `${formatTimeWithoutSeconds(displaySession.scheduled_start_time)} - ${formatTimeWithoutSeconds(displaySession.scheduled_end_time)}`,
    duration,
    amount: parseAmount(booking),
    status: isCompleted ? 'completed' : booking.status,
    paymentStatus: booking.payment_status,
    recurringInfo: isCompleted
      ? `Completed (${totalSessions} sessions)`
      : `Session ${displaySession.instance_number} of ${totalSessions}`,
    cancelledSessionsCount: cancelledCount,  // NEW: Track cancelled sessions
    type: 'recurring',
    source: 'spotts', // SPOTTS recurring bookings are always from the app
    createdAt: booking.created_at || '',
    rawData: booking,
  };
}

// ============================================================================
// COURT AVAILABILITY TRANSFORMATIONS
// ============================================================================

/**
 * Converts time string (HH:MM or HH:MM:SS) to minutes from midnight
 */
export function timeToMinutes(time: string): number {
  if (!time) return 0;
  const parts = time.split(':');
  const hours = parseInt(parts[0]) || 0;
  const minutes = parseInt(parts[1]) || 0;
  return hours * 60 + minutes;
}

/**
 * Converts minutes from midnight to HH:MM format
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

// ============================================================================
// MANUAL RECURRING BOOKING TRANSFORMATIONS
// ============================================================================

/**
 * Groups manual bookings by recurring_group_id
 * Returns both grouped bookings and standalone bookings (no recurring_group_id)
 */
export function groupManualRecurringBookings(
  bookings: OneTimeBooking[]
): { groups: ManualRecurringGroup[]; standalone: OneTimeBooking[] } {
  const groupsMap = new Map<string, OneTimeBooking[]>();
  const standalone: OneTimeBooking[] = [];

  // Separate bookings with recurring_group_id from standalone
  bookings.forEach((booking) => {
    if (booking.recurring_group_id) {
      const existing = groupsMap.get(booking.recurring_group_id) || [];
      existing.push(booking);
      groupsMap.set(booking.recurring_group_id, existing);
    } else {
      standalone.push(booking);
    }
  });

  // Transform grouped bookings into ManualRecurringGroup objects
  const groups: ManualRecurringGroup[] = [];

  groupsMap.forEach((groupBookings, groupId) => {
    // Sort bookings by date
    const sorted = [...groupBookings].sort((a, b) =>
      (a.booking_date || '').localeCompare(b.booking_date || '')
    );

    const firstBooking = sorted[0];
    const today = new Date().toISOString().split('T')[0];

    // Calculate session counts
    const completedSessions = sorted.filter(
      (b) => b.status === 'completed' || (b.booking_date && b.booking_date < today && b.status !== 'cancelled')
    ).length;
    const cancelledSessions = sorted.filter((b) => b.status === 'cancelled').length;

    // Find next upcoming session
    const upcomingSessions = sorted.filter(
      (b) => b.booking_date && b.booking_date >= today && b.status !== 'cancelled'
    );
    const nextSession = upcomingSessions[0] || null;

    // Find last session (most recent)
    const lastSession = sorted[sorted.length - 1];

    // Calculate total amount
    const totalAmount = sorted.reduce((sum, b) => sum + parseAmount(b), 0);

    groups.push({
      recurring_group_id: groupId,
      bookings: sorted,
      customer_name: firstBooking.customer_name || 'Unknown',
      customer_phone: firstBooking.customer_phone,
      customer_email: firstBooking.customer_email,
      court_name: getCourtName(firstBooking),
      sport_name: getSportName(firstBooking),
      total_sessions: sorted.length,
      completed_sessions: completedSessions,
      cancelled_sessions: cancelledSessions,
      total_amount: totalAmount,
      next_session: nextSession || undefined,
      last_session: lastSession,
      first_session_date: sorted[0]?.booking_date || '',
      last_session_date: sorted[sorted.length - 1]?.booking_date || '',
    });
  });

  return { groups, standalone };
}

/**
 * Gets the display booking for a manual recurring group (next upcoming or last session)
 */
export function getDisplayBookingForManualGroup(
  group: ManualRecurringGroup,
  today: string
): { booking: OneTimeBooking | null; isCompleted: boolean; sessionNumber: number } {
  const sortedBookings = [...group.bookings].sort((a, b) =>
    (a.booking_date || '').localeCompare(b.booking_date || '')
  );

  // Find next upcoming non-cancelled session.
  // For today: use time-based check — end_time '00:00:00' = midnight, always upcoming on start date.
  const now2 = new Date();
  const nowMins2 = now2.getHours() * 60 + now2.getMinutes();

  const upcomingBookings = sortedBookings.filter((b) => {
    if (!b.booking_date || b.status === 'cancelled') return false;
    if (b.booking_date > today) return true;
    if (b.booking_date < today) return false;
    // Today: check end time
    const endTime = b.end_time || '';
    if (!endTime || endTime.startsWith('00:00')) return true;
    const [eh, em] = endTime.split(':').map(Number);
    return (eh * 60 + (em || 0)) > nowMins2;
  });

  if (upcomingBookings.length > 0) {
    const nextBooking = upcomingBookings[0];
    const sessionNumber = sortedBookings.findIndex((b) => b.id === nextBooking.id) + 1;
    return { booking: nextBooking, isCompleted: false, sessionNumber };
  }

  // No upcoming sessions - find most recent past session
  const pastBookings = sortedBookings
    .filter((b) => b.booking_date && b.booking_date < today)
    .reverse();

  if (pastBookings.length > 0) {
    const lastBooking = pastBookings[0];
    const sessionNumber = sortedBookings.findIndex((b) => b.id === lastBooking.id) + 1;
    return { booking: lastBooking, isCompleted: true, sessionNumber };
  }

  // Fallback to first session
  const firstBooking = sortedBookings[0];
  return { booking: firstBooking || null, isCompleted: true, sessionNumber: 1 };
}

/**
 * Determines the overall status for a manual recurring group
 */
function getGroupStatus(group: ManualRecurringGroup, isCompleted: boolean, displayBooking: OneTimeBooking): BookingStatus {
  // If ALL sessions are cancelled, show cancelled
  if (group.cancelled_sessions === group.total_sessions) {
    return 'cancelled';
  }

  // If no upcoming sessions and has completed sessions, show completed
  if (isCompleted && group.completed_sessions > 0) {
    return 'completed';
  }

  // Otherwise use the display booking's status
  return displayBooking.status as BookingStatus;
}

/**
 * Generates the recurring info string showing accurate progress
 */
function getRecurringInfo(group: ManualRecurringGroup, isCompleted: boolean, sessionNumber: number): string {
  const { total_sessions, completed_sessions, cancelled_sessions } = group;

  // If all cancelled
  if (cancelled_sessions === total_sessions) {
    return `All ${total_sessions} sessions cancelled`;
  }

  // If all completed (none cancelled)
  if (isCompleted && cancelled_sessions === 0) {
    return `Completed (${completed_sessions}/${total_sessions} sessions)`;
  }

  // If completed with some cancelled
  if (isCompleted && cancelled_sessions > 0) {
    return `${completed_sessions}/${total_sessions} done, ${cancelled_sessions} cancelled`;
  }

  // Active booking - show current session
  if (cancelled_sessions > 0) {
    return `Session ${sessionNumber}/${total_sessions} (${cancelled_sessions} cancelled)`;
  }

  return `Session ${sessionNumber} of ${total_sessions}`;
}

/**
 * Transforms a ManualRecurringGroup to a BookingRow
 */
export function transformManualRecurringGroup(
  group: ManualRecurringGroup,
  today: string
): BookingRow | null {
  const { booking: displayBooking, isCompleted, sessionNumber } = getDisplayBookingForManualGroup(group, today);

  if (!displayBooking) {
    return null;
  }

  const duration = displayBooking.duration_minutes || calculateDuration(
    displayBooking.start_time,
    displayBooking.end_time
  );

  return {
    id: displayBooking.id, // Use the display booking's ID for click handling
    reference: group.recurring_group_id, // Show the group ID as reference
    sport: group.sport_name,
    customer: group.customer_name,
    court: group.court_name,
    date: displayBooking.booking_date || 'N/A',
    time: `${formatTimeWithoutSeconds(displayBooking.start_time)} - ${formatTimeWithoutSeconds(displayBooking.end_time)}`,
    duration,
    amount: group.total_amount, // Show total amount for the group
    status: getGroupStatus(group, isCompleted, displayBooking),
    paymentStatus: displayBooking.payment_status,
    recurringInfo: getRecurringInfo(group, isCompleted, sessionNumber),
    cancelledSessionsCount: group.cancelled_sessions,
    manualRecurringGroupId: group.recurring_group_id,
    type: 'manual_recurring',
    source: 'manual', // Manual recurring groups are always from vendor
    createdAt: group.bookings[0]?.created_at || '',
    rawData: group,
  };
}
